import { TRENDS } from '@/config/trends';
import { websiteConfig } from '@/config/website';
import { getLocale, localeConfig } from '@/lib/locale';
import { Routes } from '@/lib/routes';
import { getCanonicalUrl, getImageUrl } from '@/lib/urls';
import { getRamalamaCopy } from './ramalama-copy';

export const RAMALAMA_PATH = Routes.RamalamaTrend;
export const RAMALAMA_PUBLISHED = '2026-10-18';
export const RAMALAMA_LAST_UPDATED = '2026-10-18';
export const RAMALAMA_OG_IMAGE = '/ramalama-trend/ramalama-trend-og.jpg';
export const RAMALAMA_STORYBOARD_IMAGE =
  '/ramalama-trend/ramalama-trend-storyboard.jpg';

/**
 * JSON-LD for /ramalama-trend. Every text field is read from
 * `ramalama-copy.ts`, the same constants the visible page renders.
 */
export function createRamalamaJsonLd() {
  const copy = getRamalamaCopy();
  const siteName = websiteConfig.metadata?.name ?? '';
  const homeUrl = getCanonicalUrl(Routes.Root);
  const url = getCanonicalUrl(RAMALAMA_PATH);
  const organizationId = `${homeUrl}#organization`;
  const inLanguage = localeConfig[getLocale()].hreflang;
  const logoPath = websiteConfig.metadata?.images?.logoLight;
  const example = TRENDS.ramalama;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': organizationId,
        name: siteName,
        url: homeUrl,
        ...(logoPath
          ? { logo: { '@type': 'ImageObject', url: getImageUrl(logoPath) } }
          : {}),
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: copy.seoTitle,
        description: copy.seoDescription,
        inLanguage,
        datePublished: `${RAMALAMA_PUBLISHED}T00:00:00+08:00`,
        dateModified: `${RAMALAMA_LAST_UPDATED}T00:00:00+08:00`,
        isPartOf: { '@type': 'WebSite', name: siteName, url: homeUrl },
        publisher: { '@id': organizationId },
        about: {
          '@type': 'Thing',
          name: 'Ramalama (Bang Bang) Halloween costume walk trend',
        },
        mainEntity: { '@id': `${url}#software` },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: getImageUrl(RAMALAMA_OG_IMAGE),
          width: 1200,
          height: 630,
        },
        breadcrumb: { '@id': `${url}#breadcrumb` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: copy.breadcrumbHome,
            item: homeUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: copy.breadcrumbCurrent,
            item: url,
          },
        ],
      },
      {
        '@type': 'SoftwareApplication',
        '@id': `${url}#software`,
        name: copy.title,
        applicationCategory: 'MultimediaApplication',
        applicationSubCategory: 'AI video generator',
        operatingSystem: 'Web',
        description: copy.quickAnswer,
        url,
        inLanguage,
        publisher: { '@id': organizationId },
        featureList: copy.features.items.map((feature) => feature.title),
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          url: getCanonicalUrl(Routes.Pricing),
        },
      },
      {
        '@type': 'VideoObject',
        '@id': `${url}#example-video`,
        name: `${copy.breadcrumbCurrent} example: solo witch walk`,
        description: copy.storyboard.captions.join(' '),
        thumbnailUrl: getImageUrl(example.previewPoster),
        contentUrl: getImageUrl(example.previewVideo),
        uploadDate: `${RAMALAMA_PUBLISHED}T00:00:00+08:00`,
        duration: `PT${example.templateSeconds}S`,
        publisher: { '@id': organizationId },
      },
      {
        '@type': 'HowTo',
        '@id': `${url}#howto`,
        name: copy.steps.title,
        step: copy.steps.items.map((step, index) => ({
          '@type': 'HowToStep',
          position: index + 1,
          name: step.name,
          text: step.text,
        })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: copy.faqs.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
    ],
  };
}

export function RamalamaJsonLd() {
  return (
    <script
      type="application/ld+json"
      suppressHydrationWarning
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(createRamalamaJsonLd()),
      }}
    />
  );
}
